import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ModuleHeader from './components/ModuleHeader';
import { useAuth } from './AuthContext';
import BotaoArquivar from './BotaoArquivar';
import {
  ACENTO, Auditoria, BORDA, COR, Consolidado, Contagem, ItemAuditoria, LinhaConsolidado, Nivel, Opcoes, Quadro,
  RECIPIENTES, RISCO_ESCURO, S, SUB, TINTA, Tubito, botaoCor, caixa, fmtData, fmtHora, fmtIndice, fmtNum, hojeISO,
  liraaApi, mensagemErro, periodo, useCiclosLiraa,
} from './liraaApi';

// Cartao 3 do LIRAa: selo + quadro de avisos (auditoria), positividade por area e por agente,
// e o CONSOLIDADO no formato do LIRAa Digital (SESAB). Componentes PROPRIOS (mesma aparencia
// do mural do Quadro de Positividade, sem importar nada dele). Sessao 30, bloco N3 (24/09/2026).

const CSS_IMPRESSAO = `
@media print {
  @page { size: A4 landscape; margin: 10mm; }
  .lq-noprint { display: none !important; }
  .lq-raiz { background: #fff !important; padding: 0 !important; }
  .lq-print { border: none !important; box-shadow: none !important; padding: 0 !important; }
  .lq-print table { font-size: 10px; }
  .lq-print th, .lq-print td { background: #fff !important; color: #000 !important; border-color: #000 !important; }
  .lq-titulo-impressao { display: block !important; }
}`;

const REGRAS_CICLO = new Set(['A3', 'A5', 'A6', 'A11', 'A13']);           // resolvem-se em Ciclo e Equipe
const REGRAS_TUBITO = new Set(['A1', 'A2', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10', 'A11', 'A12']);

const corClass = (c: Contagem['classificacao']) =>
  c === 'satisfatório' ? COR.verde.forte : c === 'alerta' ? COR.amarelo.forte : c === 'risco' ? RISCO_ESCURO : SUB;

// ── icone do selo (SVG, sem emoji) ─────────────────────────────────────────
function IconeSelo({ nivel }: { nivel: Nivel }) {
  const p = { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: '#fff', strokeWidth: 3, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (nivel === 'verde') return <svg {...p}><polyline points="5 12.5 10 17.5 19 7" /></svg>;
  if (nivel === 'vermelho') return <svg {...p}><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>;
  return <svg {...p} strokeWidth={2.4}><circle cx="12" cy="12" r="9" /><line x1="12" y1="7.5" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12" y2="16.7" /></svg>;
}

function ItemMural({ item, tubitos, onAbrirTubito, onAbrirCiclo }: {
  item: ItemAuditoria; tubitos: Map<number, Tubito>;
  onAbrirTubito: (id: number) => void; onAbrirCiclo: () => void;
}) {
  const trava = item.nivel === 'trava';
  const c = COR[trava ? 'vermelho' : 'amarelo'];
  const ex = item.explicacao || {};
  return (
    <div style={{ background: c.fundo, borderLeft: `6px solid ${c.forte}`, borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ background: c.forte, color: '#fff', fontSize: 12, fontWeight: 800, letterSpacing: 0.6, padding: '3px 8px', borderRadius: 4 }}>
          {trava ? 'TRAVA' : 'AVISO'}
        </span>
        <span style={{ fontSize: 16, fontWeight: 800, color: c.texto, flex: '1 1 240px' }}>{ex.titulo || item.regra}</span>
        {item.area_nome && <span style={{ fontSize: 13, fontWeight: 700, color: c.texto }}>{item.area_nome}</span>}
        <span style={{ fontSize: 12, fontWeight: 700, color: c.texto, border: `1.5px solid ${c.forte}`, borderRadius: 999, padding: '2px 10px', background: '#fff' }}>{item.regra}</span>
      </div>
      <div style={{ fontSize: 15, color: TINTA, marginTop: 6 }}><strong>{item.mensagem}</strong></div>
      <details style={{ marginTop: 6 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, color: c.texto }}>Por que, causas e como corrigir</summary>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 18px', marginTop: 6, fontSize: 14, color: TINTA }}>
          <div><b style={{ color: c.texto }}>POR QUE:</b> {ex.porque || '—'}</div>
          <div><b style={{ color: c.texto }}>CAUSAS:</b> {ex.causas || '—'}</div>
          <div><b style={{ color: c.texto }}>COMO CORRIGIR:</b> {ex.corrigir || '—'}</div>
        </div>
      </details>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {REGRAS_TUBITO.has(item.regra) && item.tubito_ids.slice(0, 8).map(id => {
          const t = tubitos.get(id);
          return (
            <button key={id} style={S.botaoMini} onClick={() => onAbrirTubito(id)}>
              Abrir tubito {t ? `nº ${t.numero_amostra} (${t.agente_nome})` : ''}
            </button>
          );
        })}
        {item.tubito_ids.length > 8 && <span style={{ fontSize: 13, color: SUB, alignSelf: 'center' }}>+{item.tubito_ids.length - 8} no Laboratório</span>}
        {REGRAS_CICLO.has(item.regra) && <button style={S.botaoMini} onClick={onAbrirCiclo}>Abrir Ciclo e Equipe</button>}
      </div>
    </div>
  );
}

export default function LiraaQuadro({ onVoltar, onNavegar, onAbrirTubito }: {
  onVoltar: () => void; onNavegar: (pagina: string) => void; onAbrirTubito: (id: number) => void;
}) {
  const { token } = useAuth();
  const { ciclos, ciclo, cicloId, setCicloId, erro: erroCiclos } = useCiclosLiraa(token);
  const [opcoes, setOpcoes] = useState<Opcoes | null>(null);
  const [area, setArea] = useState('');
  const [quadro, setQuadro] = useState<Quadro | null>(null);
  const [aud, setAud] = useState<Auditoria | null>(null);
  const [estadoAud, setEstadoAud] = useState<'carregando' | 'ok' | 'falhou'>('carregando');
  const [cons, setCons] = useState<Consolidado | null>(null);
  const [tubitos, setTubitos] = useState<Map<number, Tubito>>(new Map());
  const [erro, setErro] = useState('');
  const [muralAberto, setMuralAberto] = useState(true);
  const [copiado, setCopiado] = useState('');
  // Arquivos: dados do Consolidado para o botão "Arquivar" (sem ciclo_id: o ciclo do LIRAa é de outra tabela)
  const pedidoConsolidado = useMemo(() => (cons && ciclo ? {
    pasta: 'liraa' as const, tipo: 'liraa_consolidado', chave: `liraa_consolidado|lc${ciclo.id}`,
    titulo: `LIRAa · Consolidado SESAB · ${ciclo.nome}`, referencia: `LIRAa ${ciclo.nome}`, avisos: [] as string[],
  } : null), [cons, ciclo]);

  useEffect(() => { liraaApi.opcoes(token).then(setOpcoes).catch(e => setErro(mensagemErro(e))); }, [token]);

  const carregar = useCallback(async () => {
    if (!cicloId) return;
    setEstadoAud('carregando'); setErro('');
    const areaId = area ? Number(area) : undefined;
    try {
      const [q, c, ts] = await Promise.all([
        liraaApi.quadro(token, cicloId, areaId), liraaApi.consolidado(token, cicloId), liraaApi.tubitos(token, cicloId),
      ]);
      setQuadro(q); setCons(c); setTubitos(new Map(ts.map(t => [t.id, t])));
    } catch (e) { setErro(mensagemErro(e, 'Não foi possível carregar o quadro.')); }
    try { setAud(await liraaApi.auditoria(token, cicloId)); setEstadoAud('ok'); }
    catch { setAud(null); setEstadoAud('falhou'); }
  }, [token, cicloId, area]);
  useEffect(() => { carregar(); }, [carregar]);

  const itens = useMemo(() => (aud?.itens || []).filter(i => !area || i.area_id === null || i.area_id === Number(area)), [aud, area]);
  const nTravas = itens.filter(i => i.nivel === 'trava').length;
  const nAvisos = itens.length - nTravas;
  const nivel: Nivel = estadoAud !== 'ok' ? 'cinza' : nTravas > 0 ? 'vermelho' : 'verde';
  const cs = COR[nivel];
  const tituloSelo = estadoAud === 'carregando' ? 'Auditando…'
    : estadoAud === 'falhou' ? 'Auditoria indisponível — dados não conferidos'
    : nTravas > 0 ? `${nTravas} ${nTravas === 1 ? 'trava encontrada' : 'travas encontradas'} — não digite no LIRAa Digital ainda`
    : nAvisos > 0 ? `Nenhuma trava — ${nAvisos} ${nAvisos === 1 ? 'aviso' : 'avisos'} para conferir`
    : 'Tudo certo — nenhuma divergência';

  const abrirCiclo = () => onNavegar('liraa_ciclo');

  // ── copiar / imprimir consolidado ──
  const valores = (l: LinhaConsolidado) => [
    l.bairro, fmtNum(l.imoveis_programados), fmtNum(l.imoveis_inspecionados), l.aeg_tb, l.aeg_outros, l.alb_tb, l.alb_outros,
    fmtIndice(l.iip_aeg), fmtIndice(l.ib_aeg), fmtIndice(l.iip_alb), fmtIndice(l.ib_alb),
    l.rec_aeg_A1, l.rec_aeg_A2, l.rec_aeg_B, l.rec_aeg_C, l.rec_aeg_D1, l.rec_aeg_D2, l.rec_aeg_E, l.rec_alb,
  ].map(String);
  const copiar = async () => {
    if (!cons?.linhas) return;
    const cab = ['BAIRRO/LOCALIDADE', 'PROG.', 'INSP.', 'AEG TB', 'AEG OUTROS', 'ALB TB', 'ALB OUTROS', 'IIP AEG', 'IB AEG',
      'IIP ALB', 'IB ALB', 'A1', 'A2', 'B', 'C', 'D1', 'D2', 'E', 'REC. ALBOPICTUS'];
    const texto = [cab, ...cons.linhas.map(valores)].map(r => r.join('\t')).join('\n');
    let ok = false;
    try { await navigator.clipboard.writeText(texto); ok = true; } catch {
      try {
        const ta = document.createElement('textarea'); ta.value = texto; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); ok = document.execCommand('copy'); document.body.removeChild(ta);
      } catch { ok = false; }
    }
    setCopiado(ok ? 'Tabela copiada — cole na planilha.' : 'O navegador bloqueou a cópia.');
    setTimeout(() => setCopiado(''), 3000);
  };

  // ── celulas ──
  const indiceCel = (v: number | null, insp: number | null, cor?: string) => (
    <td style={{ ...S.td, color: cor, fontWeight: cor ? 800 : 400 }} title={insp === null ? 'falta inspecionados' : undefined}>
      {insp === null ? '—' : fmtIndice(v)}
    </td>
  );
  const linhaQuadro = (rot: React.ReactNode, c: Contagem, total = false) => {
    const peso: React.CSSProperties = total ? { fontWeight: 800, background: '#dfe8f3' } : {};
    const td = (v: React.ReactNode) => <td style={{ ...S.td, ...peso }}>{v}</td>;
    return (
      <>
        <td style={{ ...S.td, ...peso, textAlign: 'left', fontWeight: 700 }}>{rot}</td>
        {td(c.tubitos.total)}
        <td style={{ ...S.td, ...peso, color: c.tubitos.pendentes ? COR.amarelo.texto : undefined, fontWeight: c.tubitos.pendentes ? 800 : peso.fontWeight }}>{c.tubitos.pendentes}</td>
        {td(c.imoveis_com_tubito)}{td(fmtNum(c.imoveis_programados))}
        <td style={{ ...S.td, ...peso, background: c.imoveis_inspecionados === null ? COR.amarelo.fundo : peso.background }}
          title={c.imoveis_inspecionados === null ? 'falta inspecionados' : undefined}>{fmtNum(c.imoveis_inspecionados)}</td>
        {td(c.imoveis_aeg.TB)}{td(c.imoveis_aeg.Outro)}
        {indiceCel(c.iip_aeg, c.imoveis_inspecionados, corClass(c.classificacao))}
        {indiceCel(c.ib_aeg, c.imoveis_inspecionados)}
        {td(c.imoveis_alb.TB)}{td(c.imoveis_alb.Outro)}
        {indiceCel(c.iip_alb, c.imoveis_inspecionados)}
        {indiceCel(c.ib_alb, c.imoveis_inspecionados)}
        {RECIPIENTES.map(r => <React.Fragment key={r}>{td(c.rec_aeg[r])}</React.Fragment>)}
        <td style={{ ...S.td, ...peso }} title={RECIPIENTES.map(r => `${r}: ${c.rec_alb[r]}`).join(' · ')}>{c.rec_alb_total}</td>
      </>
    );
  };

  const TH_SESAB: React.CSSProperties = { ...S.th, background: '#1F4E79', color: '#fff', border: '1px solid #9fb3cc', whiteSpace: 'normal', lineHeight: 1.2 };
  const TD_SESAB: React.CSSProperties = { ...S.td, border: `1px solid ${BORDA}` };

  const destinoPendencia = (p: string) =>
    /inspecionados|programados|programada/i.test(p) ? { rotulo: 'Abrir Ciclo e Equipe', pagina: 'liraa_ciclo' }
      : { rotulo: 'Abrir Laboratório LIRAa', pagina: 'liraa_laboratorio' };

  return (
    <div className="lq-raiz" style={S.pagina}>
      <style>{CSS_IMPRESSAO}{'@keyframes lqGiro { to { transform: rotate(360deg); } }'}</style>
      <div className="lq-noprint">
        <ModuleHeader
          titulo="Quadro de Positividade LIRAa"
          subtitulo="Positividade, auditoria e consolidado para o LIRAa Digital"
          icone={<AssessmentIcon />}
          accentColor={ACENTO.quadro}
          onVoltar={onVoltar}
          chips={[
            { label: 'Ciclo', value: ciclo?.nome || '—' },
            { label: 'Período', value: periodo(ciclo) },
            { label: 'Área', value: area ? (opcoes?.areas.find(a => a.id === Number(area))?.nome || '—') : 'Todas' },
          ]}
        />
      </div>
      <div style={S.miolo}>
        {erroCiclos && <div className="lq-noprint" style={caixa('vermelho')}>{erroCiclos}</div>}
        {ciclos !== null && ciclos.length === 0 && (
          <div className="lq-noprint" style={caixa('cinza')}>Nenhum ciclo ainda. Crie o ciclo no cartão "LIRAa — Ciclo e Equipe".</div>
        )}

        {ciclo && (
          <>
            {/* 1) SELO — primeiro elemento */}
            <div className="lq-noprint" style={{ background: cs.fundo, border: `2px solid ${cs.forte}`, borderRadius: 14, padding: '14px 16px',
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: cs.forte, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {estadoAud === 'carregando'
                  ? <span style={{ width: 28, height: 28, borderRadius: '50%', border: '4px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', animation: 'lqGiro 0.9s linear infinite', display: 'block' }} />
                  : <IconeSelo nivel={nivel} />}
              </div>
              <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: cs.texto }}>Auditoria do módulo realizada</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: cs.texto, lineHeight: 1.2 }}>{tituloSelo}</span>
                <span style={{ fontSize: 15, color: SUB }}>
                  {estadoAud === 'ok' ? `Conferida em ${fmtHora(aud?.auditado_em)} · ${quadro?.total.tubitos.total ?? 0} tubitos · ${quadro?.total.tubitos.pendentes ?? 0} pendentes`
                    : estadoAud === 'falhou' ? 'O sistema não respondeu. Não digite no LIRAa Digital antes de conferir.' : 'Conferindo o ciclo…'}
                </span>
              </div>
              <button style={S.botao} onClick={carregar} disabled={estadoAud === 'carregando'}>Refazer auditoria</button>
            </div>

            {/* 2) QUADRO DE AVISOS */}
            {estadoAud === 'ok' && (
              <div className="lq-noprint" style={{ ...S.card, border: `2px solid ${BORDA}` }}>
                <button style={{ ...S.botao, border: 'none', padding: 0, background: 'transparent', fontSize: 16 }} onClick={() => setMuralAberto(a => !a)}>
                  {muralAberto ? '▾' : '▸'} QUADRO DE AVISOS DE DIVERGÊNCIAS
                  <span style={{ fontSize: 14, fontWeight: 700, color: SUB }}>({nTravas} {nTravas === 1 ? 'trava' : 'travas'} · {nAvisos} {nAvisos === 1 ? 'aviso' : 'avisos'})</span>
                </button>
                {muralAberto && (
                  <div style={{ marginTop: 10 }}>
                    {itens.length === 0 && <div style={caixa('verde')}>✓ A auditoria não encontrou divergências{area ? ' nesta área' : ''}.</div>}
                    {itens.map((i, k) => (
                      <ItemMural key={k} item={i} tubitos={tubitos} onAbrirTubito={onAbrirTubito} onAbrirCiclo={abrirCiclo} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* filtros */}
            <div className="lq-noprint" style={{ ...S.card, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ flex: '1 1 240px' }}><span style={S.rotulo}>🔁 Ciclo LIRAa</span>
                <select style={S.campo} value={cicloId ?? ''} onChange={e => setCicloId(Number(e.target.value) || null)}>
                  {ciclos?.map(c => <option key={c.id} value={c.id}>{c.nome}{c.em_andamento ? ' (em andamento)' : ''}</option>)}
                </select></label>
              <label style={{ flex: '1 1 200px' }}><span style={S.rotulo}>📍 Área</span>
                <select style={S.campo} value={area} onChange={e => setArea(e.target.value)}>
                  <option value="">Todas</option>{opcoes?.areas.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                </select></label>
            </div>
            {erro && <div className="lq-noprint" style={caixa('vermelho')}>{erro}</div>}

            {/* 3) POR AREA */}
            {quadro && (
              <div className="lq-noprint" style={{ ...S.card, overflowX: 'auto' }}>
                <h3 style={S.tituloSecao}>Positividade por área</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Área', 'Tubitos', 'Pendentes', 'Imóveis c/ tubito', 'Programados', 'Inspecionados'].map(hh => <th key={hh} rowSpan={2} style={S.th}>{hh}</th>)}
                      <th colSpan={4} style={{ ...S.th, background: '#fde8e8' }}><i>Ae. aegypti</i></th>
                      <th colSpan={4} style={{ ...S.th, background: '#e7f3ea' }}><i>Ae. albopictus</i></th>
                      <th colSpan={7} style={S.th}>Recipientes <i>Ae. aegypti</i></th>
                      <th rowSpan={2} style={S.th}>Recip. <i>albopictus</i></th>
                    </tr>
                    <tr>{['TB', 'Outros', 'IIP', 'IB', 'TB', 'Outros', 'IIP', 'IB', ...RECIPIENTES].map((hh, i) => <th key={i} style={S.th}>{hh}</th>)}</tr>
                  </thead>
                  <tbody>
                    {quadro.areas.length === 0 && <tr><td colSpan={22} style={{ ...S.td, color: SUB }}>Nenhuma área com programação ou tubito.</td></tr>}
                    {quadro.areas.map(a => <tr key={a.area_id}>{linhaQuadro(a.area_nome, a)}</tr>)}
                    {quadro.areas.length > 0 && <tr>{linhaQuadro('TOTAL', quadro.total, true)}</tr>}
                  </tbody>
                </table>
                <div style={{ fontSize: 13, color: SUB, marginTop: 8 }}>
                  Cor do IIP <i>Ae. aegypti</i>: <b style={{ color: COR.verde.forte }}>satisfatório</b> (&lt; 1) · <b style={{ color: COR.amarelo.forte }}>alerta</b> (1 a 3,99) · <b style={{ color: RISCO_ESCURO }}>risco</b> (≥ 4). Índice "—" = falta informar os inspecionados.
                </div>
              </div>
            )}

            {/* 4) POR AGENTE */}
            {quadro && quadro.por_agente.length > 0 && (
              <div className="lq-noprint" style={{ ...S.card, overflowX: 'auto' }}>
                <h3 style={S.tituloSecao}>Por agente</h3>
                <table style={{ borderCollapse: 'collapse', minWidth: 560 }}>
                  <thead><tr>{['Agente', 'Tubitos', 'Pendentes', 'Tubitos + aegypti', 'Tubitos + albopictus', 'Imóveis + aegypti', 'Imóveis + albopictus'].map(hh => <th key={hh} style={S.th}>{hh}</th>)}</tr></thead>
                  <tbody>{quadro.por_agente.map(a => (
                    <tr key={a.agente_id}>
                      <td style={{ ...S.td, textAlign: 'left', fontWeight: 700 }}>{a.agente_nome}</td>
                      <td style={S.td}>{a.tubitos}</td>
                      <td style={{ ...S.td, color: a.pendentes ? COR.amarelo.texto : undefined, fontWeight: a.pendentes ? 800 : 400 }}>{a.pendentes}</td>
                      <td style={S.td}>{a.tubitos_positivos_aeg}</td><td style={S.td}>{a.tubitos_positivos_alb}</td>
                      <td style={S.td}>{a.imoveis_positivos_aeg}</td><td style={S.td}>{a.imoveis_positivos_alb}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}

            {/* 5) CONSOLIDADO SESAB */}
            {cons && (
              <div className="lq-print" style={S.card}>
                <h3 className="lq-noprint" style={S.tituloSecao}>CONSOLIDADO — formato LIRAa Digital (SESAB)</h3>
                {!cons.pronto && (
                  <div style={caixa('vermelho')}>
                    <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>Consolidado ainda não pode ser digitado</div>
                    {cons.pendencias.map((p, i) => {
                      const d = destinoPendencia(p);
                      return (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '4px 0' }}>
                          <span style={{ flex: '1 1 300px' }}>• {p}</span>
                          <button style={S.botaoMini} onClick={() => onNavegar(d.pagina)}>{d.rotulo}</button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {cons.pronto && cons.linhas && cons.rodape && (
                  <>
                    <div className="lq-titulo-impressao" style={{ display: 'none', marginBottom: 8, color: '#000' }}>
                      <div style={{ fontSize: 16, fontWeight: 800 }}>LIRAa — Consolidado (formato LIRAa Digital / SESAB)</div>
                      <div style={{ fontSize: 12 }}>Ciclo: {ciclo.nome} · Período: {periodo(ciclo)} · Emitido em {fmtData(hojeISO())}</div>
                    </div>
                    <div className="lq-noprint" style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button style={botaoCor(ACENTO.quadro)} onClick={copiar}>📋 Copiar tabela</button>
                      <button style={S.botao} onClick={() => window.print()}>🖨 Imprimir</button>
                      <BotaoArquivar alvo={() => document.querySelector('.lq-print')} pedido={pedidoConsolidado} />
                      {copiado && <span style={{ fontWeight: 700, color: COR.verde.texto }}>{copiado}</span>}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', color: TINTA }}>
                        <thead>
                          <tr>
                            <th rowSpan={2} style={{ ...TH_SESAB, minWidth: 150 }}>BAIRRO/LOCALIDADE</th>
                            <th colSpan={2} style={TH_SESAB}>IMÓVEIS</th>
                            <th colSpan={2} style={TH_SESAB}>IMÓVEIS AE. AEG</th>
                            <th colSpan={2} style={TH_SESAB}>IMÓVEIS AE. ALB</th>
                            <th colSpan={4} style={TH_SESAB}>ÍNDICE %</th>
                            <th colSpan={7} style={TH_SESAB}>TIPO DE RECIPIENTE PARA AEDES AEGYPTI</th>
                            <th rowSpan={2} style={TH_SESAB}>RECIPIENTE AEDES ALBOPICTUS</th>
                          </tr>
                          <tr>{['PROG.', 'INSP.', 'TB', 'OUTROS', 'TB', 'OUTROS', 'IIP AEG', 'IB AEG', 'IIP ALB', 'IB ALB', ...RECIPIENTES].map((hh, i) => <th key={i} style={TH_SESAB}>{hh}</th>)}</tr>
                        </thead>
                        <tbody>
                          {cons.linhas.map((l, i) => (
                            <tr key={i}>
                              {valores(l).map((v, j) => <td key={j} style={{ ...TD_SESAB, textAlign: j === 0 ? 'left' : 'center', fontWeight: j === 0 ? 700 : 400 }}>{v}</td>)}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr><td colSpan={19} style={{ ...TD_SESAB, textAlign: 'left', fontWeight: 700, whiteSpace: 'normal' }}>
                            Total de bairros/localidades: {cons.rodape.total_bairros} · Total de imóveis programados: {fmtNum(cons.rodape.total_programados)} · Total de imóveis inspecionados: {cons.rodape.total_inspecionados}
                          </td></tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
